import React from 'react'
import { Box, Columns, Element, Heading } from 'react-bulma-components'
import { WorkerCounter } from '../../api/worker-counters'
import {
  formatDuration,
  timeInputValueToHoursAsNumber,
  hoursAsNumberToTimeInputValue,
  timeInputValueToDuration,
} from '../../utils/date'
import { useAdjustValueMutation } from '../../queries/worker-counters'
import { RequestButton } from '../request-components/request-components'
import InputField from '../form/fields/input'
import SelectField from '../form/fields/select'
import { Formik, Form, Field } from 'formik'
import { isNumber } from 'lodash'

interface WorkerCounterAdjustFormProps {
  workerCounter: WorkerCounter
}

/**
 * Composant pour ajuster la valeur d'un compteur de travailleur
 * Permet d'ajouter, soustraire ou forcer une nouvelle valeur
 */
const WorkerCounterAdjustForm: React.FC<WorkerCounterAdjustFormProps> = ({ workerCounter }) => {
  const adjustValueMutation = useAdjustValueMutation()

  /**
   * Calcule la valeur finale et l'ajustement pour un type d'opération donné
   * @param adjustmentType - Type d'ajustement ('add', 'subtract', 'force')
   * @param valueInMs - Valeur d'entrée en millisecondes
   * @returns Objet contenant l'ajustement à envoyer à l'API et la valeur finale
   */
  const calculateAdjustment = (adjustmentType: string, valueInMs: number) => {
    const currentValueInMs = workerCounter.currentValue
    let adjustmentValue: number
    let finalValueInMs: number

    switch (adjustmentType) {
      case 'add':
        adjustmentValue = valueInMs
        finalValueInMs = currentValueInMs + valueInMs
        break
      case 'subtract':
        adjustmentValue = -valueInMs
        finalValueInMs = currentValueInMs - valueInMs
        break
      case 'force':
        adjustmentValue = valueInMs - currentValueInMs
        finalValueInMs = valueInMs
        break
      default:
        throw new Error(`Type d'ajustement invalide: ${adjustmentType}`)
    }

    return { adjustmentValue, finalValueInMs }
  }

  /**
   * Validation Formik pour le formulaire d'ajustement
   */
  const validateForm = (values: { hours: string; minutes: string; adjustmentType: string }) => {
    const errors: { hours?: string; minutes?: string } = {}

    // Vérification des champs vides
    if (!values.hours && !values.minutes) {
      errors.hours = 'La durée est requise'
      return errors
    }

    // Conversion des valeurs
    const hours = parseInt(values.hours || '0', 10)
    const minutes = parseInt(values.minutes || '0', 10)

    if (isNaN(hours) || hours < 0) {
      errors.hours = 'Heures invalides'
    }
    if (isNaN(minutes) || minutes < 0 || minutes > 59) {
      errors.minutes = 'Minutes invalides (0-59)'
    }

    const valueInMs = (hours * 60 + minutes) * 60 * 1000
    if (valueInMs <= 0) {
      errors.hours = 'La durée doit être supérieure à 0'
    }

    return errors
  }

  /**
   * Gère la soumission du formulaire d'ajustement
   */
  const handleAdjustValue = async (values: {
    hours: string
    minutes: string
    adjustmentType: string
  }) => {
    try {
      const hours = parseInt(values.hours || '0', 10)
      const minutes = parseInt(values.minutes || '0', 10)
      const valueInMs = (hours * 60 + minutes) * 60 * 1000
      if (valueInMs > 0) {
        const { adjustmentValue } = calculateAdjustment(values.adjustmentType, valueInMs)
        await adjustValueMutation.mutateAsync({
          id: workerCounter._id,
          value: adjustmentValue,
        })
      }
    } catch (error) {
      console.error("Erreur lors de l'ajustement:", error)
    }
  }

  return (
    <Box>
      <Heading size={5} mb={3}>
        Ajuster la valeur
      </Heading>
      {/*
        Formulaire Formik avec deux champs séparés : heures et minutes
      */}
      <Formik
        initialValues={{ hours: '', minutes: '0', adjustmentType: 'subtract' }}
        onSubmit={handleAdjustValue}
        validate={validateForm}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => {
          let valueInMs: number | undefined
          let finalValueInMs: number | undefined
          try {
            const hours = parseInt(values.hours || '0', 10)
            const minutes = parseInt(values.minutes || '0', 10)
            if (!isNaN(hours) && hours >= 0 && !isNaN(minutes) && minutes >= 0 && minutes < 60) {
              valueInMs = (hours * 60 + minutes) * 60 * 1000
              if (valueInMs > 0) {
                const { finalValueInMs: calculatedFinalValue } = calculateAdjustment(
                  values.adjustmentType,
                  valueInMs,
                )
                finalValueInMs = calculatedFinalValue
              }
            }
          } catch (error) {
            // Valeur invalide, on ignore
          }
          return (
            <Form>
              {/* Champs heures et minutes */}
              <Columns>
                <Columns.Column size={4}>
                  <Field
                    name="adjustmentType"
                    component={SelectField}
                    label="Type d'ajustement"
                    options={[
                      { value: 'add', label: 'Ajouter +' },
                      { value: 'subtract', label: 'Diminuer -' },
                      { value: 'force', label: 'Forcer une valeur' },
                    ]}
                  />
                </Columns.Column>
                <Columns.Column size={4}>
                  <Field
                    name="hours"
                    component={InputField}
                    label="Heures"
                    type="number"
                    min={0}
                    step={1}
                    placeholder="0"
                    required
                  />
                </Columns.Column>
                <Columns.Column size={4}>
                  <Field
                    name="minutes"
                    component={InputField}
                    label="Minutes"
                    type="number"
                    min={0}
                    max={59}
                    step={1}
                    placeholder="00"
                    required
                  />
                </Columns.Column>
              </Columns>
              {/* Aperçu du résultat */}
              {Boolean(!!values.hours || !!values.minutes) &&
                isNumber(valueInMs) &&
                valueInMs > 0 &&
                !errors.hours &&
                !errors.minutes && (
                  <Element mb={3}>
                    <Element
                      className={`notification ${errors.hours || errors.minutes ? 'is-danger' : 'is-info'} is-light`}
                    >
                      <strong>Résultat :</strong>{' '}
                      {errors.hours || errors.minutes ? (
                        errors.hours || errors.minutes
                      ) : (
                        <>
                          {values.adjustmentType === 'add' &&
                            `${formatDuration(workerCounter.currentValue)} + ${formatDuration(valueInMs)} = ${formatDuration(finalValueInMs!)}`}
                          {values.adjustmentType === 'subtract' &&
                            `${formatDuration(workerCounter.currentValue)} - ${formatDuration(valueInMs)} = ${formatDuration(finalValueInMs!)}`}
                          {values.adjustmentType === 'force' && `${formatDuration(valueInMs)}`}
                        </>
                      )}
                    </Element>
                  </Element>
                )}
              <RequestButton
                request={adjustValueMutation}
                type="submit"
                disabled={
                  isSubmitting ||
                  (!values.hours && !values.minutes) ||
                  !valueInMs ||
                  valueInMs <= 0 ||
                  !!errors.hours ||
                  !!errors.minutes
                }
                color="primary"
              >
                {values.adjustmentType === 'add' && 'Ajouter'}
                {values.adjustmentType === 'subtract' && 'Soustraire'}
                {values.adjustmentType === 'force' && 'Forcer la valeur'}
              </RequestButton>
            </Form>
          )
        }}
      </Formik>
    </Box>
  )
}

export default WorkerCounterAdjustForm
